import {
    createContext,
    useContext,
    useMemo,
    useState,
    useCallback,
    useRef,
    useEffect,
} from "react";
import { createSeededNoise } from "../../utils/perlinNoise";
import {
    luminanceToPaletteFill,
    luminanceToPaletteIndex,
    paletteBucketMidpoint,
    escapeXmlAttr,
    DEFAULT_LUMINANCE_PALETTE,
    equalSplitThresholds,
} from "../../utils/luminancePalette";
import { mergedBlobOutlinePathDs } from "../../utils/gridBlobOutline";
import { offsetClosedPathDs } from "../../utils/blobPathOffset";
import {
    buildLuminanceCellPathD,
    type CellCornerMode,
} from "../../utils/luminanceCellShape";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export type BlobRegionMode = "dark" | "light";
export type { CellCornerMode };

/** One merged-outline pass: its own threshold, region, offset, and stroke. */
export interface BlobOutlineLayer {
    id: string;
    blobRegion: BlobRegionMode;
    blobThreshold: number;
    blobOutlineOffset: number;
    blobStrokeWidth: number;
    blobStrokeColor: string;
}

export interface BlobOutlineLayerGroup {
    id: string;
    paths: string[];
    strokeWidth: number;
    strokeColor: string;
}

/** Cap total cells so the SVG stays responsive in the browser. */
const MAX_GRID_PIXELS = 320_000;

export interface LuminanceCell {
    x: number;
    y: number;
    fill: string;
    /** Bevel/round shape; omit for axis-aligned rectangle */
    pathD?: string;
}

/** Scales down colsxrows proportionally so the product does not exceed maxPixels. */
function clampGridProduct(
    cols: number,
    rows: number,
    maxPixels: number
): { cols: number; rows: number } {
    let c = Math.max(1, Math.round(cols));
    let r = Math.max(1, Math.round(rows));
    if (c * r <= maxPixels) {
        return { cols: c, rows: r };
    }
    const s = Math.sqrt((c * r) / maxPixels);
    c = Math.max(1, Math.floor(c / s));
    r = Math.max(1, Math.floor(r / s));
    while (c * r > maxPixels) {
        if (c >= r) c--;
        else r--;
    }
    return { cols: c, rows: r };
}

interface LuminanceGridState {
    imageDataUrl: string | null;
    imageName: string | null;
    sourceWidth: number | null;
    sourceHeight: number | null;
    cols: number;
    rows: number;
    effectiveCols: number;
    effectiveRows: number;
    gridProductCapped: boolean;
    matchRowsToImageAspect: boolean;
    noiseAmount: number;
    noiseScale: number;
    seed: string;
    invert: boolean;
    paletteEnabled: boolean;
    paletteColors: string[];
    /** Length `paletteColors.length - 1` when ≥2 colors; boundaries between adjacent colors. */
    paletteThresholds: number[];
    blobOutlineEnabled: boolean;
    blobOutlineLayers: BlobOutlineLayer[];
    /** When palette has 2+ colors: if true, blob mask uses palette bucket midpoints; if false, raw luminance `l`. */
    blobQuantizeToPalette: boolean;
    blobOutlineLayerGroups: BlobOutlineLayerGroup[];
    cellCornerMode: CellCornerMode;
    cellCornerSize: number;
    cells: LuminanceCell[];
    viewBoxAttr: string;
    canExport: boolean;

    setNoiseAmount: (amount: number) => void;
    setNoiseScale: (scale: number) => void;
    setSeed: (seed: string) => void;
    setInvert: (invert: boolean) => void;
    setPaletteEnabled: (enabled: boolean) => void;
    setPaletteColors: (colors: string[]) => void;
    setPaletteThresholds: (thresholds: number[]) => void;
    setBlobOutlineEnabled: (enabled: boolean) => void;
    setBlobOutlineLayers: React.Dispatch<
        React.SetStateAction<BlobOutlineLayer[]>
    >;
    updateBlobOutlineLayer: (
        id: string,
        patch: Partial<Omit<BlobOutlineLayer, "id">>
    ) => void;
    addBlobOutlineLayer: () => void;
    removeBlobOutlineLayer: (id: string) => void;
    setBlobQuantizeToPalette: (quantize: boolean) => void;
    setCols: (cols: number) => void;
    setRows: (rows: number) => void;
    setMatchRowsToImageAspect: (match: boolean) => void;
    setCellCornerMode: (mode: CellCornerMode) => void;
    setCellCornerSize: (size: number) => void;
    handleImageLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    imageInputRef: React.RefObject<HTMLInputElement>;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const LuminanceGridContext = createContext<LuminanceGridState | null>(null);

export function useLuminanceGrid() {
    const context = useContext(LuminanceGridContext);
    if (!context) {
        throw new Error(
            "useLuminanceGrid must be used within LuminanceGridProvider"
        );
    }
    return context;
}

interface LuminanceGridProviderProps {
    children: React.ReactNode;
}

const DEFAULT_LUMINANCE_BLOB_LAYERS: BlobOutlineLayer[] = [
    {
        id: "layer-default",
        blobRegion: "dark",
        blobThreshold: 0.5,
        blobOutlineOffset: 0,
        blobStrokeWidth: 0.06,
        blobStrokeColor: "#141414",
    },
];

function defaultLuminanceGridPreset(): Record<string, unknown> {
    return {
        cols: 50,
        rows: 50,
        matchRowsToImageAspect: false,
        noiseAmount: 0,
        noiseScale: 0.1,
        seed: "",
        invert: false,
        paletteEnabled: false,
        paletteColors: [...DEFAULT_LUMINANCE_PALETTE],
        paletteThresholds: equalSplitThresholds(
            DEFAULT_LUMINANCE_PALETTE.length,
        ),
        blobOutlineEnabled: false,
        blobOutlineLayers: DEFAULT_LUMINANCE_BLOB_LAYERS.map((l) => ({
            ...l,
        })),
        blobQuantizeToPalette: true,
        cellCornerMode: "square" as CellCornerMode,
        cellCornerSize: 0.12,
    };
}

export function LuminanceGridProvider({ children }: LuminanceGridProviderProps) {
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [imageName, setImageName] = useState<string | null>(null);
    const [sourceWidth, setSourceWidth] = useState<number | null>(null);
    const [sourceHeight, setSourceHeight] = useState<number | null>(null);
    const [cols, setCols] = useState(50);
    const [rows, setRows] = useState(50);
    const [matchRowsToImageAspect, setMatchRowsToImageAspect] =
        useState(false);
    const [noiseAmount, setNoiseAmount] = useState(0);
    const [noiseScale, setNoiseScale] = useState(0.1);
    const [seed, setSeed] = useState("");
    const [invert, setInvert] = useState(false);
    const [paletteEnabled, setPaletteEnabled] = useState(false);
    const [paletteColors, setPaletteColors] = useState<string[]>(() => [
        ...DEFAULT_LUMINANCE_PALETTE,
    ]);
    const [paletteThresholds, setPaletteThresholds] = useState<number[]>(() =>
        equalSplitThresholds(DEFAULT_LUMINANCE_PALETTE.length)
    );
    const [blobOutlineEnabled, setBlobOutlineEnabled] = useState(false);
    const [blobOutlineLayers, setBlobOutlineLayers] = useState<
        BlobOutlineLayer[]
    >(() => [
        {
            id: "layer-default",
            blobRegion: "dark",
            blobThreshold: 0.5,
            blobOutlineOffset: 0,
            blobStrokeWidth: 0.06,
            blobStrokeColor: "#141414",
        },
    ]);
    const [blobQuantizeToPalette, setBlobQuantizeToPalette] = useState(true);

    const updateBlobOutlineLayer = useCallback(
        (id: string, patch: Partial<Omit<BlobOutlineLayer, "id">>) => {
            setBlobOutlineLayers((prev) =>
                prev.map((l) => (l.id === id ? { ...l, ...patch } : l))
            );
        },
        []
    );

    const addBlobOutlineLayer = useCallback(() => {
        setBlobOutlineLayers((prev) => {
            const last = prev[prev.length - 1];
            return [
                ...prev,
                {
                    ...last,
                    id: `layer-${Date.now().toString(36)}`,
                },
            ];
        });
    }, []);

    const removeBlobOutlineLayer = useCallback((id: string) => {
        setBlobOutlineLayers((prev) =>
            prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)
        );
    }, []);
    const [cellCornerMode, setCellCornerMode] =
        useState<CellCornerMode>("square");
    const [cellCornerSize, setCellCornerSize] = useState(0.12);
    const [brightnessData, setBrightnessData] = useState<number[] | null>(null);

    const imageInputRef = useRef<HTMLInputElement | null>(null);

    const { cols: gridCols, rows: gridRows } = useMemo(
        () => clampGridProduct(cols, rows, MAX_GRID_PIXELS),
        [cols, rows]
    );
    const gridProductCapped = gridCols !== cols || gridRows !== rows;

    useEffect(() => {
        if (!matchRowsToImageAspect) return;
        if (
            sourceWidth == null ||
            sourceHeight == null ||
            sourceWidth <= 0 ||
            sourceHeight <= 0
        ) {
            return;
        }
        const nextRows = Math.max(
            1,
            Math.round(cols * (sourceHeight / sourceWidth))
        );
        setRows((prev) => (prev === nextRows ? prev : nextRows));
    }, [
        matchRowsToImageAspect,
        cols,
        sourceWidth,
        sourceHeight,
    ]);

    const sampleBrightness = useCallback(
        (dataUrl: string, targetCols: number, targetRows: number) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = targetCols;
                canvas.height = targetRows;
                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(img, 0, 0, targetCols, targetRows);
                const imageData = ctx.getImageData(
                    0,
                    0,
                    targetCols,
                    targetRows
                );
                const data = imageData.data;

                const brightness: number[] = [];
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    brightness.push(
                        (0.299 * r + 0.587 * g + 0.114 * b) / 255
                    );
                }
                setBrightnessData(brightness);
            };
            img.src = dataUrl;
        },
        []
    );

    const handleImageLoad = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;
            if (!file.type.startsWith("image/")) return;

            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl =
                    typeof reader.result === "string" ? reader.result : "";
                if (!dataUrl) return;

                const img = new Image();
                img.onload = () => {
                    const nw = img.naturalWidth;
                    const nh = img.naturalHeight;
                    setSourceWidth(nw);
                    setSourceHeight(nh);
                    setImageDataUrl(dataUrl);
                    setImageName(file.name);
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(file);
        },
        []
    );

    useEffect(() => {
        if (imageDataUrl) {
            sampleBrightness(imageDataUrl, gridCols, gridRows);
        }
    }, [imageDataUrl, gridCols, gridRows, sampleBrightness]);

    const { cells, blobOutlineLayerGroups } = useMemo(() => {
        if (
            !brightnessData ||
            brightnessData.length !== gridCols * gridRows
        ) {
            return {
                cells: [] as LuminanceCell[],
                blobOutlineLayerGroups: [] as BlobOutlineLayerGroup[],
            };
        }

        const seededNoise = createSeededNoise(seed || "luminance-grid");
        const out: LuminanceCell[] = [];
        const fills: string[] = new Array(gridCols * gridRows);
        const compareLFlat: number[] | null = blobOutlineEnabled
            ? new Array(gridCols * gridRows)
            : null;

        for (let row = 0; row < gridRows; row++) {
            for (let col = 0; col < gridCols; col++) {
                const idx = row * gridCols + col;
                let l = brightnessData[idx];

                if (noiseAmount > 0) {
                    const noise =
                        (seededNoise(col * noiseScale, row * noiseScale) -
                            0.5) *
                        2 *
                        noiseAmount;
                    l = Math.max(0, Math.min(1, l + noise));
                }

                if (invert) {
                    l = 1 - l;
                }

                const fill = luminanceToPaletteFill(
                    l,
                    paletteEnabled && paletteColors.length > 0
                        ? paletteColors
                        : [],
                    paletteEnabled && paletteColors.length >= 2
                        ? paletteThresholds
                        : undefined
                );

                fills[idx] = fill;

                if (blobOutlineEnabled && compareLFlat) {
                    let compareL: number;
                    if (
                        blobQuantizeToPalette &&
                        paletteEnabled &&
                        paletteColors.length >= 2
                    ) {
                        const pIdx = luminanceToPaletteIndex(
                            l,
                            paletteColors,
                            paletteThresholds
                        );
                        compareL = paletteBucketMidpoint(
                            pIdx,
                            paletteColors.length,
                            paletteThresholds
                        );
                    } else {
                        compareL = l;
                    }
                    compareLFlat[idx] = compareL;
                }
            }
        }

        for (let row = 0; row < gridRows; row++) {
            for (let col = 0; col < gridCols; col++) {
                const idx = row * gridCols + col;
                const fill = fills[idx];
                const pathD = buildLuminanceCellPathD(
                    col,
                    row,
                    gridCols,
                    gridRows,
                    cellCornerSize,
                    cellCornerMode,
                    fills
                );

                out.push({
                    x: col,
                    y: row,
                    fill,
                    ...(pathD ? { pathD } : {}),
                });
            }
        }

        const blobOutlineLayerGroups: BlobOutlineLayerGroup[] = [];
        if (blobOutlineEnabled && compareLFlat) {
            for (const layer of blobOutlineLayers) {
                const filled: boolean[][] = Array.from(
                    { length: gridRows },
                    () => Array<boolean>(gridCols).fill(false)
                );
                for (let row = 0; row < gridRows; row++) {
                    for (let col = 0; col < gridCols; col++) {
                        const compareL = compareLFlat[row * gridCols + col];
                        filled[row][col] =
                            layer.blobRegion === "dark"
                                ? compareL <= layer.blobThreshold
                                : compareL >= layer.blobThreshold;
                    }
                }
                const rawBlobPaths = mergedBlobOutlinePathDs(
                    gridCols,
                    gridRows,
                    filled
                );
                const paths =
                    layer.blobOutlineOffset !== 0
                        ? offsetClosedPathDs(
                              rawBlobPaths,
                              layer.blobOutlineOffset
                          )
                        : rawBlobPaths;
                blobOutlineLayerGroups.push({
                    id: layer.id,
                    paths,
                    strokeWidth: layer.blobStrokeWidth,
                    strokeColor: layer.blobStrokeColor,
                });
            }
        }

        return { cells: out, blobOutlineLayerGroups };
    }, [
        brightnessData,
        gridCols,
        gridRows,
        noiseAmount,
        noiseScale,
        seed,
        invert,
        paletteEnabled,
        paletteColors,
        paletteThresholds,
        blobOutlineEnabled,
        blobOutlineLayers,
        blobQuantizeToPalette,
        cellCornerMode,
        cellCornerSize,
    ]);

    const viewBoxAttr = `0 0 ${gridCols} ${gridRows}`;
    const canExport = cells.length > 0;

    const buildExportSvgString = useCallback(() => {
        if (!cells.length) return "";

        const useCellPaths = cells.some((c) => c.pathD);
        const cellMarkup = cells
            .map((c) =>
                c.pathD
                    ? `    <path d="${escapeXmlAttr(c.pathD)}" fill="${escapeXmlAttr(c.fill)}" />`
                    : `    <rect x="${c.x}" y="${c.y}" width="1" height="1" fill="${escapeXmlAttr(c.fill)}" />`
            )
            .join("\n");

        const blobGroups =
            blobOutlineEnabled &&
            blobOutlineLayerGroups.some((g) => g.paths.length > 0)
                ? blobOutlineLayerGroups
                      .filter((g) => g.paths.length > 0)
                      .map(
                          (g) =>
                              `\n  <g class="luminance-grid-blobs" fill="none" stroke="${escapeXmlAttr(g.strokeColor)}" stroke-width="${g.strokeWidth}" stroke-linejoin="round" stroke-linecap="round" shape-rendering="geometricPrecision">\n${g.paths
                                  .map(
                                      (d) =>
                                          `    <path d="${escapeXmlAttr(d)}" />`
                                  )
                                  .join("\n")}\n  </g>`
                      )
                      .join("")
                : "";

        const shapeRender = useCellPaths
            ? "geometricPrecision"
            : "crispEdges";

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}" shape-rendering="${shapeRender}">
  <g class="luminance-grid">
${cellMarkup}
  </g>${blobGroups}
</svg>`;
    }, [cells, viewBoxAttr, blobOutlineEnabled, blobOutlineLayerGroups]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        const baseName = imageName
            ? imageName.replace(/\.[^.]+$/, "")
            : "luminance-grid";
        link.download = `${baseName}-luminance-${gridCols}x${gridRows}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, imageName, gridCols, gridRows]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            cols, rows, matchRowsToImageAspect, noiseAmount, noiseScale, seed,
            invert, paletteEnabled, paletteColors, paletteThresholds,
            blobOutlineEnabled, blobOutlineLayers, blobQuantizeToPalette,
            cellCornerMode, cellCornerSize,
        }),
        [
            cols, rows, matchRowsToImageAspect, noiseAmount, noiseScale, seed,
            invert, paletteEnabled, paletteColors, paletteThresholds,
            blobOutlineEnabled, blobOutlineLayers, blobQuantizeToPalette,
            cellCornerMode, cellCornerSize,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.cols != null) setCols(values.cols as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.matchRowsToImageAspect != null) setMatchRowsToImageAspect(values.matchRowsToImageAspect as boolean);
        if (values.noiseAmount != null) setNoiseAmount(values.noiseAmount as number);
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.invert != null) setInvert(values.invert as boolean);
        if (values.paletteEnabled != null) setPaletteEnabled(values.paletteEnabled as boolean);
        if (values.paletteColors != null) setPaletteColors(values.paletteColors as string[]);
        if (values.paletteThresholds != null) setPaletteThresholds(values.paletteThresholds as number[]);
        if (values.blobOutlineEnabled != null) setBlobOutlineEnabled(values.blobOutlineEnabled as boolean);
        if (values.blobOutlineLayers != null) setBlobOutlineLayers(values.blobOutlineLayers as BlobOutlineLayer[]);
        if (values.blobQuantizeToPalette != null) setBlobQuantizeToPalette(values.blobQuantizeToPalette as boolean);
        if (values.cellCornerMode != null) setCellCornerMode(values.cellCornerMode as CellCornerMode);
        if (values.cellCornerSize != null) setCellCornerSize(values.cellCornerSize as number);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "luminance-grid",
        defaultPreset: defaultLuminanceGridPreset,
        getPresetValues,
        applyPreset,
    });

    const value: LuminanceGridState = {
        imageDataUrl,
        imageName,
        sourceWidth,
        sourceHeight,
        cols,
        rows,
        effectiveCols: gridCols,
        effectiveRows: gridRows,
        gridProductCapped,
        matchRowsToImageAspect,
        noiseAmount,
        noiseScale,
        seed,
        invert,
        paletteEnabled,
        paletteColors,
        paletteThresholds,
        blobOutlineEnabled,
        blobOutlineLayers,
        blobQuantizeToPalette,
        blobOutlineLayerGroups,
        cellCornerMode,
        cellCornerSize,
        cells,
        viewBoxAttr,
        canExport,
        setNoiseAmount,
        setNoiseScale,
        setSeed,
        setInvert,
        setPaletteEnabled,
        setPaletteColors,
        setPaletteThresholds,
        setBlobOutlineEnabled,
        setBlobOutlineLayers,
        updateBlobOutlineLayer,
        addBlobOutlineLayer,
        removeBlobOutlineLayer,
        setBlobQuantizeToPalette,
        setCols,
        setRows,
        setMatchRowsToImageAspect,
        setCellCornerMode,
        setCellCornerSize,
        handleImageLoad,
        buildExportSvgString,
        handleDownload,
        imageInputRef,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <LuminanceGridContext.Provider value={value}>
            {children}
        </LuminanceGridContext.Provider>
    );
}
